import type React from 'react';
import classNames from 'classnames';

import ActionsDropdown from 'components/partials/actions-dropdown';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { renderModal } from 'helpers/modal-render';
import { formatDate } from 'helpers/dates-formatter';

import { useApiDeleteConversation } from '../../hooks/use-api-delete-conversation';

import Icons from 'assets/icons';

interface Session {
  id: string;
  name: string;
  createdAt: string;
}

interface SessionItemProps {
  session: Session;
  isActive: boolean;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  isReadOnly?: boolean;
}

const SessionItem = ({
  session,
  isActive,
  setSearchParams,
  clientId,
  isReadOnly,
}: SessionItemProps): React.JSX.Element => {
  const currentUser = useCurrentUser();
  const { mutateAsync: deleteConversation } = useApiDeleteConversation({
    oktaUserId: currentUser.data?.oktaUserId as string,
    clientId,
  });

  const handleConfirmDelete = async (): Promise<void> => {
    await deleteConversation(session.id).then(() => setSearchParams({}));
  };

  const handleChatClick = (): void => {
    setSearchParams({ conversationId: session.id });
  };

  return (
    <div
      className={classNames(
        'flex items-center justify-between px-2 py-2 rounded-md hover:bg-gray-100',
        { 'bg-gray-100': isActive },
      )}
    >
      <button
        type="button"
        className="min-w-0 flex-1 text-left bg-transparent border-none cursor-pointer p-0"
        onClick={handleChatClick}
      >
        <p className="text-gray-700 truncate">{session.name}</p>
        <p className="text-gray-600 text-sm">
          {formatDate(session.createdAt, 'MMM D, YYYY h:mm A')}
        </p>
      </button>

      {!isReadOnly && (
        <ActionsDropdown
          options={[
            {
              label: 'Delete chat',
              startIcon: <Icons.Delete />,
              onClick: () =>
                renderModal({
                  headerText: `Are you sure you want to delete this chat?`,
                  headerSubtext: `This chat will be permanently removed from your session history. You won't be able to restore it.`,
                  onConfirmCallback: handleConfirmDelete,
                  onConfirmButtonLabel: 'Delete chat',
                }),
            },
          ]}
          variant="reserve-text"
          customClass="hover:!bg-gray-200 py-0 !px-1"
          menuPosition="left"
        />
      )}
    </div>
  );
};

export default SessionItem;
